import { nextTick } from 'vue'
import { defineRouter } from '#q-app/wrappers'
import {
  createRouter,
  createMemoryHistory,
  createWebHistory,
  createWebHashHistory,
} from 'vue-router'
import routes from 'src/router/routes'
import { ROUTES } from 'src/router/const'
import { handleNavigationError } from 'src/boot/version-control'
import { posthog } from 'src/boot/posthog'
import {
  hasPracticeRouteId,
  isPracticeActive,
  isPracticeId,
  PRACTICE_ROUTE_QUERY_KEYS,
} from 'src/composables/onboarding/practice/practiceMode'

/*
 * If not building with SSR mode, you can
 * directly export the Router instantiation;
 *
 * The function below can be async too; either use
 * async/await or return a Promise which resolves
 * with the Router instance.
 */

// Create a variable to hold the router instance
let router

export default defineRouter(function (/* { store, ssrContext } */) {
  const createHistory = process.env.SERVER
    ? createMemoryHistory
    : process.env.VUE_ROUTER_MODE === 'history'
      ? createWebHistory
      : createWebHashHistory

  router = createRouter({
    scrollBehavior(to, from, savedPosition) {
      if (savedPosition) {
        return savedPosition
      }
      return false
    },
    routes,

    // Leave this as is and make changes in quasar.conf.js instead!
    // quasar.conf.js -> build -> vueRouterMode
    // quasar.conf.js -> build -> publicPath
    history: createHistory(process.env.VUE_ROUTER_BASE),
  })

  // Catch chunk load errors on every failed navigation (first and retries); registered here so we always have the router instance
  router.onError((error) => {
    handleNavigationError(error)
    throw error
  })

  // Stale example ids: with no walkthrough running, an example id in the URL is dropped, so a reload
  // or a shared link lands on a clean screen. The HTTP guard is the backstop.
  router.beforeEach((to) => {
    if (isPracticeActive.value || !to.name || !hasPracticeRouteId(to)) return undefined
    if (isPracticeId(to.params.ticketId)) return { name: ROUTES.HOME, replace: true }

    const query = { ...to.query }
    PRACTICE_ROUTE_QUERY_KEYS.forEach((key) => {
      if (isPracticeId(query[key])) delete query[key]
    })
    return { name: to.name, params: to.params, query, hash: to.hash, replace: true }
  })

  // Track page navigation with time spent (including query-based modals/drawers)
  let pageEnterTime = Date.now()

  router.beforeEach((to, from) => {
    if (!from?.path || !from?.name) return
    if (!isPageChange(to, from)) return
    // Leaving an example screen records no page leave. Every other move records as usual.
    if (hasPracticeRouteId(from)) return
    const timeOnPage = Date.now() - pageEnterTime
    const fromPageId = getPageId(from)
    posthog?.capture('$pageleave', {
      path: fromPageId,
      name: from.name,
      ticketId: from.query.ticketId,
      approvalId: from.query.approvalId,
      time_on_page_ms: timeOnPage,
      time_on_page_seconds: Math.round(timeOnPage / 1000),
    })
  })
  router.afterEach(async (to, from) => {
    // Scroll layout container to top when path changes (scrollable element is .q-page-container, not window)
    if (isPageChange(to, from)) {
      await nextTick()
      document.querySelector('.q-page-container')?.scrollTo({ top: 0 })
    } else {
      return
    }

    const now = Date.now()

    // Track page view for new page, except an example screen (it carries a practice id)
    if (!hasPracticeRouteId(to)) {
      posthog?.capture('$pageview', {
        path: getPageId(to),
        name: to.name,
        ticketId: to.query.ticketId,
        approvalId: to.query.approvalId,
      })
    }

    // Reset timer for new page
    pageEnterTime = now
  })

  return router
})

function getPageId(route) {
  if (route.query.ticketId) {
    return `${route.path}?ticketId=${route.query.ticketId}`
  }
  if (route.query.approvalId) {
    return `${route.path}?approvalId=${route.query.approvalId}`
  }
  return route.path
}

function isPageChange(to, from) {
  const pathChanged = to.path !== from.path
  const ticketChanged = to.query.ticketId !== from.query.ticketId
  const approvalChanged = to.query.approvalId !== from.query.approvalId
  return pathChanged || ticketChanged || approvalChanged
}

export { router }
